import type { MangoAccount, MangoCache, MangoGroup, MarketKind } from "@blockworks-foundation/mango-client";
import {
  getAllMarkets,
  getMarketByBaseSymbolAndKind,
  getMultipleAccounts,
  getTokenBySymbol as getMangoTokenBySymbol,
  MangoClient,
  PerpMarket,
  PerpMarketLayout,
  ZERO_BN,
} from "@blockworks-foundation/mango-client";
import { Market } from "@project-serum/serum";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { AccountInfo } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { BN } from "bn.js";
import { useEffect, useMemo, useState } from "react";

import { useNetworkConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { DEFAULT_MANGO_GROUP_CONFIG, defaultMangoGroupIds } from "../../models/mango";
import type { MarginAccount, MarginOpenOrder, MarginPerpetualPosition, MarginPosition } from "../../types/margin";
import { getMarginPositionIcon } from "../../utils/margin";
import { getBalances } from "../../utils/margin/mango/balances";
import { getOpenOrders, isPerpTriggerOrder } from "../../utils/margin/mango/open-orders";
import { getPerpPositions } from "../../utils/margin/mango/perpetuals";
import { fetchTradeHistory, getTradeHistory } from "../../utils/margin/mango/trade-history";
import { zipDict } from "../../utils/margin/mango/utils";
import { notify } from "../../utils/notifications";
import { getTokenByMint } from "../../utils/tokenInfo";

export interface AccountInfoList {
  [key: string]: AccountInfo<Buffer>;
}

const initialMarket = {
  base: "BTC",
  kind: "perp",
  name: "BTC-PERP",
  path: "/market?name=BTC-PERP",
};
const marketConfig = getMarketByBaseSymbolAndKind(
  DEFAULT_MANGO_GROUP_CONFIG!,
  initialMarket.base,
  initialMarket.kind as MarketKind,
);

const programId = new PublicKey(defaultMangoGroupIds.mangoProgramId);
const mangoGroupPk = new PublicKey(defaultMangoGroupIds.publicKey);

export const useMangoMarkets = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { network } = useNetworkConfig();

  const client = useMemo(() => new MangoClient(connection, programId), [connection]);
  const { priceInUSD } = useMarkets();
  const mangoGroupConfig = DEFAULT_MANGO_GROUP_CONFIG;
  const [marginAccounts, setMarginAccounts] = useState<MarginAccount[]>([]);
  const [mangoAccounts, setMangoAccounts] = useState<MangoAccount[]>([]);
  const [spotMarkets, setSpotMarkets] = useState<Market[]>([]);
  const [mangoGroup, setMangoGroup] = useState<MangoGroup>();
  const [tradeHistoryMap, setTradeHistoryMap] = useState<{ [account: string]: any[] }>({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const isMainnet = network === "mainnet-beta";
  const refreshMango = () => {
    setRefreshCounter(refreshCounter + 1);
  };

  useEffect(() => {
    setTradeHistoryMap({});
    setMarginAccounts([]);
    setMangoAccounts([]);
    setSpotMarkets([]);
    setMangoGroup(undefined);
  }, [connected]);

  useEffect(() => {
    void (async () => {
      if (connected && publicKey && isMainnet) {
        const mangoGroup = await client.getMangoGroup(mangoGroupPk);
        const mangoAccounts = await client.getMangoAccountsForOwner(mangoGroup, publicKey, true);
        const tradeHistories = await Promise.all(
          mangoAccounts.map(async (account) => {
            const history = await fetchTradeHistory(account);
            return {
              account: account.publicKey.toBase58(),
              history,
            };
          }),
        );

        setTradeHistoryMap(
          tradeHistories.reduce((acc, val) => {
            acc[val.account] = val.history;
            return {
              ...acc,
            };
          }, {}),
        );
      }
    })();
  }, [connected, client, isMainnet, publicKey]);

  useEffect(() => {
    if (connected && publicKey && mangoGroupConfig && isMainnet) {
      void (async () => {
        const mangoGroup = await client.getMangoGroup(mangoGroupPk);
        setMangoGroup(mangoGroup);

        const mangoAccounts = await client.getMangoAccountsForOwner(mangoGroup, publicKey, true);
        setMangoAccounts(mangoAccounts);
        const allMarketConfigs = getAllMarkets(mangoGroupConfig);
        const allMarketPks = allMarketConfigs.map((m) => m.publicKey);
        let allMarketAccountInfos;
        let mangoCache: MangoCache;
        try {
          const resp = await Promise.all([
            getMultipleAccounts(connection, allMarketPks),
            mangoGroup.loadCache(connection),
            mangoGroup.loadRootBanks(connection),
          ]);
          allMarketAccountInfos = resp[0];
          mangoCache = resp[1];
        } catch {
          notify({
            type: "error",
            message: "Failed to load the mango group. Please refresh.",
          });
        }
        const allMarketAccounts = allMarketConfigs.map((config, i) => {
          if (config.kind === "spot") {
            const decoded = Market.getLayout(programId).decode(allMarketAccountInfos[i].accountInfo.data);
            return new Market(
              decoded,
              config.baseDecimals,
              config.quoteDecimals,
              undefined,
              mangoGroupConfig.serumProgramId,
            );
          }
          if (config.kind === "perp") {
            const decoded = PerpMarketLayout.decode(allMarketAccountInfos[i].accountInfo.data);
            return new PerpMarket(config.publicKey, config.baseDecimals, config.quoteDecimals, decoded);
          }
          return undefined;
        });
        const allBidsAndAsksPks = allMarketConfigs.map((m) => [m.bidsKey, m.asksKey]).flat();
        const allBidsAndAsksAccountInfos = await getMultipleAccounts(connection, allBidsAndAsksPks);
        let slot = 0;
        const accountInfos: AccountInfoList = {};
        const allMarkets = zipDict(
          allMarketPks.map((pk) => pk.toBase58()),
          allMarketAccounts,
        );
        const mangoSpotMarkets = Object.values(allMarkets).filter((mkt) => mkt instanceof Market) as Market[];
        setSpotMarkets(mangoSpotMarkets);
        allBidsAndAsksAccountInfos.forEach(({ publicKey, context, accountInfo }) => {
          slot = context.slot;
          accountInfos[publicKey.toBase58()] = accountInfo;
        });

        allMarketAccountInfos.concat(allBidsAndAsksAccountInfos).forEach(({ publicKey, context, accountInfo }) => {
          if (context.slot >= slot) {
            slot = context.slot;
            accountInfos[publicKey.toBase58()] = accountInfo;
          }
        });

        const market = allMarkets[marketConfig.publicKey.toBase58()];
        let loadedFills: any[] = [];
        try {
          loadedFills = (await market?.loadFills(connection, 10000)) ?? [];
        } catch (err) {
          console.error("useMangoMarkets Error:", err);
          captureException(err);
          loadedFills = [];
        }
        const marginAccounts = mangoAccounts.reduce<MarginAccount[]>((accounts, mangoAccount) => {
          const tradeHistoryData = tradeHistoryMap[mangoAccount.publicKey.toBase58()] ?? [];
          const tradeHistory = getTradeHistory({
            marketConfig,
            fills: loadedFills,
            mangoAccount,
            selectedMangoGroup: mangoGroup,
            mangoGroupConfig,
            tradeHistory: tradeHistoryData,
          });
          const perpPositions = getPerpPositions({
            mangoAccount,
            groupConfig: mangoGroupConfig,
            mangoGroup,
            mangoCache,
            allMarkets,
            tradeHistory,
          });
          const balances = getBalances(mangoGroup, mangoAccount, mangoGroupConfig, mangoCache);
          const openOrders = getOpenOrders({
            markets: allMarkets,
            mangoGroup,
            mangoAccount,
            groupConfig: mangoGroupConfig,
            accountInfos,
          });

          const mangoPerps = perpPositions.openPositions.reduce<MarginPerpetualPosition[]>((positions, position) => {
            if (!position.marketConfig || !position.perpAccount) {
              return positions;
            }

            const side = position.perpAccount.basePosition.gt(ZERO_BN) ? "long" : "short";
            const symbol = position.marketConfig.baseSymbol;
            const logoURI = getMarginPositionIcon(symbol);
            return positions.concat({
              kind: "Mango Markets",
              market: position.marketConfig.name,
              side,
              positionSize: position.basePosition,
              notionalSize: position.notionalSize,
              avgEntryPrice: position.avgEntryPrice,
              breakEvenPrice: position.breakEvenPrice,
              symbol,
              logoURI,
              baseDecimals: position.marketConfig.baseDecimals || 6,
              unrealizedPnl: position.unrealizedPnl,
            });
          }, []);

          const mangoOrders = openOrders.map<MarginOpenOrder>(({ order, market }) => {
            const price = BN.isBN(order.price) ? order.price.toNumber() : order.price;
            const size = isPerpTriggerOrder(order) ? 0 : order.size;
            const symbol = market.config.baseSymbol;
            const logoURI = getMarginPositionIcon(symbol);
            return {
              price,
              kind: "Mango Markets",
              size: price * size,
              amount: size,
              side: order.side,
              symbol,
              logoURI,
              market: market.config.name,
              baseDecimals: market.config.baseDecimals,
            };
          });
          const mangoBalances = balances
            .filter(
              (b) =>
                (b.deposits && b.deposits.toNumber() > 0) ||
                (b.borrows && b.borrows.toNumber() > 0) ||
                (b.orders && b.orders > 0) ||
                (b.unsettled && b.unsettled > 0),
            )
            .map<MarginPosition>((balance) => {
              const mint = getMangoTokenBySymbol(mangoGroupConfig, balance.symbol).mintKey.toBase58();
              const logoURI = getTokenByMint(mint)?.logoURI;
              return {
                mint,
                logoURI,
                deposit: balance.deposits?.toNumber() ?? 0,
                borrow: balance.borrows?.toNumber() ?? 0,
                net: balance.net?.toNumber() ?? 0,
                borrowApy: balance.borrowRate?.toNumber() ?? 0,
                depositApy: balance.depositRate?.toNumber() ?? 0,
                inOrders: balance.orders ?? 0,
                symbol: balance.symbol,
                unsettled: balance.unsettled ?? 0,
              };
            });

          const totalEarnedPerYear = mangoBalances.reduce((acc, val) => {
            const interest = val.net < 0 ? val.borrowApy : val.depositApy;
            return (acc += (interest / 100) * (val.net * (priceInUSD(val.mint) ?? 0)));
          }, 0);

          const equity = mangoAccount.computeValue(mangoGroup, mangoCache).toNumber();
          const totalInterest = totalEarnedPerYear / equity;
          const leverage = mangoAccount.getLeverage(mangoGroup, mangoCache).toNumber();
          const healthRatio = mangoAccount.getHealthRatio(mangoGroup, mangoCache, "Maint");

          const account: MarginAccount = {
            kind: "Mango Markets",
            pubkey: mangoAccount.publicKey,
            name: mangoAccount.name,
            balances: mangoBalances,
            orders: mangoOrders,
            perpetuals: mangoPerps,
            leverage,
            collateralRatio: healthRatio.toNumber(),
            equity,
            interest: totalInterest,
          };
          return accounts.concat(account);
        }, []);
        setMarginAccounts(marginAccounts);
      })();
    }
  }, [
    connected,
    client,
    connection,
    isMainnet,
    mangoGroupConfig,
    priceInUSD,
    tradeHistoryMap,
    publicKey,
    refreshCounter,
  ]);

  return {
    marginAccounts,
    mangoAccounts,
    spotMarkets,
    mangoGroup,
    mangoClient: client,
    refreshMango,
  };
};
